import moment from 'moment';
import set from 'lodash/set';

import utils from '../../../lib/utils';

/*
  CAUTION!!!!!

  MODIFY THIS FILE ONLY IF YOU KNOW WHAT YOU DOING!!!

  BECAUSE THIS FILE CALCS ALL AMOUNTS AND TAXES
*/

export function createLocalWithHolds(correctConcepts) {
  const plain = correctConcepts.reduce((accum, { localTaxWithHolds = [] }) => accum.concat(
    localTaxWithHolds.map(({ ...props }) => props),
  ), []).filter(tax => tax.fee);

  const localWithHolds = plain.reduce((accum, tax) => {
    const index = accum.findIndex(currentTax => currentTax.tax === tax.tax);

    if (index >= 0) {
      const taxItem = accum[index];

      const amount = parseFloat(taxItem.amount || 0) + parseFloat(tax.amount || 0);
      set(
        accum,
        `[${index}].amount`,
        amount.toFixed(6),
      );
    } else {
      tax.amount = parseFloat(tax.amount).toFixed(6);
      accum.push(tax);
    }

    return accum;
  }, []).map(({ fee, ...tax }) => ({ ...tax, fee: parseFloat(fee || 0).toFixed(6) }));

  return localWithHolds;
}

export function createTransfers(correctConcepts, decimals = 6, isRep = false) {
  let plain;

  if (isRep) {
    plain = correctConcepts.reduce((accum, { transfers = [] }) => accum.concat(
      transfers.map(({ ...props }) => props),
    ), []);
  } else {
    plain = correctConcepts.reduce((accum, { taxTransfers = [] }) => accum.concat(
      taxTransfers.map(({ ...props }) => props),
    ), []).filter(tax => tax.fee || tax.fee === 0 || tax.fee === null);
  }

  const dataTransfers = plain.reduce((accum, tax) => {
    const index = accum.findIndex(currentTax => currentTax.fee === tax.fee
      && currentTax.tax === tax.tax
      && currentTax.factorType === tax.factorType);

    if (index >= 0) {
      const taxItem = accum[index];

      const amount = tax.amount !== null
        ? parseFloat(taxItem.amount || 0) + parseFloat(tax.amount) : 0;
      const base = parseFloat(taxItem.base || 0) + parseFloat(tax.base || 0);

      accum[index].amount = amount;
      accum[index].base = base;
    } else {
      accum.push(tax);
    }

    return accum;
  }, []).map(({
    fee,
    amount,
    base,
    ...tax
  }) => ({
    ...tax,
    fee: (tax.factorType !== 'Exento') ? parseFloat(fee || 0).toFixed(6) : null,
    amount: (tax.factorType !== 'Exento')
      ? parseFloat(amount || 0).toFixed(decimals) : null,
    base: parseFloat(base || 0).toFixed(decimals),
  }));

  return dataTransfers;
}

export function createLocalTransfers(correctConcepts) {
  const plain = correctConcepts.reduce((accum, { localTaxTransfers = [] }) => accum.concat(
    localTaxTransfers.map(({ ...props }) => props),
  ), []).filter(tax => tax.fee);

  const dataLocalTransfers = plain.reduce((accum, tax) => {
    const index = accum.findIndex(currentTax => currentTax.tax === tax.tax);

    if (index >= 0) {
      const taxItem = accum[index];

      const amount = parseFloat(taxItem.amount || 0) + parseFloat(tax.amount || 0);

      set(
        accum,
        `[${index}].amount`,
        amount.toFixed(6),
      );
    } else {
      accum.push(tax);
    }

    return accum;
  }, []).map(({ fee, ...tax }) => ({ ...tax, fee: parseFloat(fee || 0).toFixed(6) }));

  return dataLocalTransfers;
}

export function createRetained(correctConcepts, decimals, isRep = false) {
  const decimalDigits = typeof decimals !== 'undefined' && !Number.isNaN(decimals) ? decimals : 6;
  let plain;

  if (isRep) {
    plain = correctConcepts.reduce((accum, { retained = [] }) => accum.concat(
      retained.map(({ ...props }) => props),
    ), []);
  } else {
    plain = correctConcepts.reduce((accum, { retainedTaxes = [] }) => accum.concat(
      retainedTaxes.map(({ ...props }) => props),
    ), []);
  }

  const dataRetained = plain.reduce((accum, tax) => {
    const index = accum.findIndex(currentTax => currentTax.tax === tax.tax);

    if (index >= 0) {
      const taxItem = accum[index];

      const amount = parseFloat(taxItem.amount || 0) + parseFloat(tax.amount || 0);
      const base = parseFloat(taxItem.base || 0) + parseFloat(tax.base || 0);

      accum[index].amount = amount;
      accum[index].base = base;
    } else {
      accum.push(tax);
    }

    return accum;
  }, []).map(
    ({ amount, base, ...tax }) => ({
      ...tax,
      amount: parseFloat(amount || 0).toFixed(decimalDigits),
      base: parseFloat(base || 0).toFixed(decimalDigits),
    }),
  );

  return dataRetained;
}

export function fixConcepts(concepts, isSaleNote, currency) {
  return concepts.map((concept) => {
    const conceptAmount = utils.standardRounding(
      parseFloat(concept.unit) * parseFloat(concept.quantity),
      currency,
    );

    const taxTransfers = Array.isArray(concept.taxTransfers)
      ? concept.taxTransfers.reduce((accum, tax) => {
        if (tax.fee) {
          accum.push({
            ...tax,
            fee: parseFloat(tax.fee).toFixed(6),
            base: conceptAmount,
            amount: utils.standardRounding(conceptAmount * parseFloat(tax.fee), currency),
          });
        } else {
          accum.push({
            ...tax,
            fee: tax.factorType === 'Exento' ? null : parseFloat(0).toFixed(6),
            base: conceptAmount,
            amount: tax.factorType === 'Exento' ? null : '0.00',
          });
        }

        return accum;
      }, []) : [];

    const localTaxTransfers = Array.isArray(concept.localTaxTransfers)
      ? concept.localTaxTransfers.reduce((accum, tax) => {
        if (tax.fee) {
          accum.push({
            ...tax,
            fee: parseFloat(tax.fee).toFixed(6),
            base: conceptAmount,
            amount: utils.standardRounding(conceptAmount * parseFloat(tax.fee), currency),
          });
        } else {
          accum.push({
            ...tax,
            fee: parseFloat(0).toFixed(6),
            base: conceptAmount,
            amount: '0.00',
          });
        }

        return accum;
      }, []) : [];

    const localTaxWithHolds = Array.isArray(concept.localTaxWithHolds)
      ? concept.localTaxWithHolds.reduce((accum, tax) => {
        if (tax.fee) {
          accum.push({
            ...tax,
            fee: parseFloat(tax.fee).toFixed(6),
            base: conceptAmount,
            amount: utils.standardRounding(conceptAmount * parseFloat(tax.fee), currency),
          });
        } else {
          accum.push({
            ...tax,
            fee: parseFloat(0).toFixed(6),
            base: conceptAmount,
            amount: '0.00',
          });
        }

        return accum;
      }, []) : [];

    const retainedTaxes = Array.isArray(concept.retainedTaxes) ? concept.retainedTaxes.map(tax => ({
      ...tax,
      fee: parseFloat(tax.fee).toFixed(6),
      base: conceptAmount,
      amount: utils.standardRounding(conceptAmount * parseFloat(tax.fee), currency),
    })) : [];

    const conceptWithTax = {
      ...concept,
      amount: conceptAmount,
    };

    if (taxTransfers.length > 0 && !isSaleNote) {
      conceptWithTax.taxTransfers = taxTransfers;
    } else {
      delete conceptWithTax.taxTransfers;
    }

    if (localTaxTransfers.length > 0 && !isSaleNote) {
      conceptWithTax.localTaxTransfers = localTaxTransfers;
    } else {
      delete conceptWithTax.localTaxTransfers;
    }

    if (localTaxWithHolds.length > 0 && !isSaleNote) {
      conceptWithTax.localTaxWithHolds = localTaxWithHolds;
    } else {
      delete conceptWithTax.localTaxWithHolds;
    }

    if (retainedTaxes.length > 0 && !isSaleNote) {
      conceptWithTax.retainedTaxes = retainedTaxes;
    } else {
      delete conceptWithTax.retainedTaxes;
    }

    return conceptWithTax;
  });
}

export function taxesFromArray(taxes, unit, quantity, currency) {
  if (Array.isArray(taxes)) {
    return taxes.reduce((accum, tax) => {
      const base = utils.standardRounding(unit * quantity, currency);

      const roundedAmount = utils.standardRounding(base * parseFloat(tax.fee || 0), currency);

      return accum + parseFloat(roundedAmount);
    }, 0);
  }

  return 0;
}

export function conceptTaxes(concept, currency) {
  const {
    taxTransfers,
    retainedTaxes,
    localTaxTransfers,
    localTaxWithHolds,
    unit,
    quantity,
  } = concept;

  const transfers = taxesFromArray(taxTransfers, unit, quantity, currency);
  const retained = taxesFromArray(retainedTaxes, unit, quantity, currency);
  const localTransfers = taxesFromArray(localTaxTransfers, unit, quantity, currency);
  const localRetained = taxesFromArray(localTaxWithHolds, unit, quantity, currency);

  return (transfers + localTransfers) - retained - localRetained;
}

const taxesProportion = (taxesDocumentRelated, proportion) => (
  Array.isArray(taxesDocumentRelated) ? taxesDocumentRelated.reduce((accum, tax) => {
    const base = (tax.previousBase * proportion).toFixed(6);

    accum.push({
      ...tax,
      base,
      amount: tax.previousAmount !== null
        ? (base * tax.fee).toFixed(6)
        : tax.previousAmount,
    });

    return accum;
  }, []) : []);

export function fixDocumentsRelated(documentsRelated) {
  return documentsRelated ? documentsRelated.map((documentRelated) => {
    const proportion = documentRelated.documentAmountPaid / documentRelated.documentTotal;

    const taxTransfers = taxesProportion(
      documentRelated.documentTaxTransfers,
      proportion,
    );

    const taxRetained = taxesProportion(
      documentRelated.documentTaxWithholdings,
      proportion,
    );

    const documentRelatedWithTax = {
      ...documentRelated,
      documentTaxTransfers: taxTransfers,
      documentTaxWithholdings: taxRetained,
    };

    return documentRelatedWithTax;
  }) : [];
}

const fixedDocumentsRelated = ({ complement }) => fixDocumentsRelated(
  complement.payments[0].documentsRelated,
);

const paymentTransfers = documentsRelated => documentsRelated.reduce(
  (accum, {
    documentTaxTransfers = [],
    documentExchangeRate,
  }) => accum.concat(
    documentTaxTransfers.map(({ ...props }) => ({
      ...props,
      amount: props.amount !== null
        ? parseFloat(props.amount) / parseFloat(documentExchangeRate || 0) : null,
      base: parseFloat(props.base || 0) / parseFloat(documentExchangeRate || 0),
    })),
  ), [],
);

const paymentRetained = documentsRelated => documentsRelated.reduce(
  (accum, {
    documentTaxWithholdings = [],
    documentExchangeRate,
  }) => accum.concat(
    documentTaxWithholdings.map(({ ...props }) => ({
      ...props,
      amount: parseFloat(props.amount || 0) / parseFloat(documentExchangeRate || 0),
      base: parseFloat(props.base || 0) / parseFloat(documentExchangeRate || 0),
    })),
  ), [],
);

export function fixPayments(payments) {
  return payments.map((payment) => {
    const documentsRelated = fixDocumentsRelated(payment.documentsRelated);

    const paymentTaxes = {
      transfers: paymentTransfers(documentsRelated),
      retained: paymentRetained(documentsRelated),
    };

    const fixPayment = {
      ...payment,
      taxes: paymentTaxes,
      documentsRelated,
    };

    return fixPayment;
  });
}

const fixedPayments = ({ complement }) => fixPayments(
  complement.payments,
);

export function createTotalTaxesPayments(payments, decimals) {
  const totalTaxes = {
    baseIVA16Transfers: null,
    amountIVA16Transfers: null,
    baseIVA8Transfers: null,
    amountIVA8Transfers: null,
    baseIVA0Transfers: null,
    amountIVA0Transfers: null,
    baseIVAExentoTransfers: null,
    IVAWithholding: null,
    ISRWithholding: null,
    IEPSWithholding: null,
    totalAmountPayments: null,
  };

  payments.forEach((payment) => {
    const exchangeRate = payment.exchangeRate && payment.currency !== 'MXN'
      ? parseFloat(payment.exchangeRate) : 1;

    if (payment.taxes && Object.keys(payment.taxes).length !== 0) {
      if (payment.taxes.transfers && Array.isArray(payment.taxes.transfers)) {
        payment.taxes.transfers.forEach((paymentTaxes) => {
          const base = Number((paymentTaxes.base * exchangeRate).toFixed(decimals));
          const amount = Number((paymentTaxes.amount * exchangeRate).toFixed(decimals));

          if (paymentTaxes.tax === '002' && paymentTaxes.fee === '0.160000') {
            totalTaxes.baseIVA16Transfers += base;
            totalTaxes.amountIVA16Transfers += amount;
          }

          if (paymentTaxes.tax === '002' && paymentTaxes.fee === '0.080000') {
            totalTaxes.baseIVA8Transfers += base;
            totalTaxes.amountIVA8Transfers += amount;
          }

          if (paymentTaxes.tax === '002' && paymentTaxes.fee === '0.000000') {
            totalTaxes.baseIVA0Transfers += base;
            totalTaxes.amountIVA0Transfers += amount;
          }

          if (paymentTaxes.tax === '002' && paymentTaxes.factorType === 'Exento') {
            totalTaxes.baseIVAExentoTransfers += base;
          }
        });
      }

      if (payment.taxes.retained && Array.isArray(payment.taxes.retained)) {
        payment.taxes.retained.forEach((paymentTaxes) => {
          const amount = Number((paymentTaxes.amount * exchangeRate).toFixed(decimals));

          if (paymentTaxes.tax === '001') {
            totalTaxes.ISRWithholding += amount;
          }

          if (paymentTaxes.tax === '002') {
            totalTaxes.IVAWithholding += amount;
          }

          if (paymentTaxes.tax === '003') {
            totalTaxes.IEPSWithholding += amount;
          }
        });
      }
    }

    const hasIVATransfer = payment.taxes.transfers.some(taxe => taxe.tax === '002');
    const hasIEPSTransfer = payment.taxes.transfers.some(taxe => taxe.tax === '003');

    if (hasIEPSTransfer && !hasIVATransfer) {
      totalTaxes.baseIVA16Transfers = 0;
      totalTaxes.amountIVA16Transfers = 0;
    }

    totalTaxes.totalAmountPayments += Number((payment.amount * exchangeRate).toFixed(decimals));
  });

  return totalTaxes;
}

const subTotalReducer = (concepts = [], currency) => {
  const sum = concepts.reduce((accum, { quantity, unit }) => (
    parseFloat(accum) + parseFloat(utils.standardRounding(quantity * unit, currency))
  ), 0);

  return sum;
};

const subTotal = ({ concepts, voucherCurrency }) => (
  subTotalReducer(concepts, voucherCurrency)
);

const subTotalCurrency = ({ voucherCurrency }, getters) => (
  utils.toCurrency(getters.subTotal, voucherCurrency)
);

const taxes = (state, getters) => {
  const totalTaxesTransferredFixed = parseFloat(utils.standardRounding(
    getters.totalTaxesTransferred,
    state.voucherCurrency,
  ));

  const totalLocalTaxesTransferredFixed = parseFloat(utils.standardRounding(
    getters.totalLocalTaxesTransferred,
    state.voucherCurrency,
  ));

  const totalLocalWithHoldsFixed = parseFloat(utils.standardRounding(
    getters.totalLocalTaxesWithHold,
    state.voucherCurrency,
  ));

  const totalTaxesWithheldFixed = parseFloat(utils.standardRounding(
    getters.totalTaxesWithheld,
    state.voucherCurrency,
  ));

  const transferred = totalTaxesTransferredFixed + totalLocalTaxesTransferredFixed;
  const holds = totalTaxesWithheldFixed + totalLocalWithHoldsFixed;
  const totalTaxes = transferred - holds;

  return parseFloat(utils.standardRounding(totalTaxes, state.voucherCurrency));
};

const taxesCurrency = ({ voucherCurrency }, getters) => (
  utils.toCurrency(getters.taxes, voucherCurrency)
);

const fixedConcepts = ({
  concepts, isSaleNote, voucherCurrency,
}) => fixConcepts(concepts, isSaleNote, voucherCurrency);

const transfers = (state, getters) => createTransfers(getters.fixedConcepts);
const retained = (state, getters) => createRetained(getters.fixedConcepts);
const localTransfers = (state, getters) => createLocalTransfers(getters.fixedConcepts);
const localRetained = (state, getters) => createLocalWithHolds(getters.fixedConcepts);

const totalTaxesTransferred = (state, getters) => getters.transfers.reduce(
  (accum, tax) => parseFloat(accum) + parseFloat(tax.amount || 0),
  0,
);

const totalLocalTaxesTransferred = (state, getters) => getters.localTransfers.reduce(
  (accum = 0, tax) => parseFloat(accum) + parseFloat(tax.amount),
  0,
);

const totalTaxesWithheld = (state, getters) => getters.retained.reduce(
  (accum, tax) => parseFloat(accum) + parseFloat(tax.amount),
  0,
);

const totalLocalTaxesWithHold = (state, getters) => getters.localRetained.reduce(
  (accum, tax) => parseFloat(accum) + parseFloat(tax.amount),
  0,
);

function total(state, getters) {
  return utils.fixTotals(getters.subTotal + getters.taxes, state.voucherCurrency);
}

const totalCurrency = ({ voucherCurrency }, getters) => (
  utils.toCurrency(getters.total, voucherCurrency)
);

const paymentsCurrencies = ({ operation }) => operation.payments.reduce((accum, { currency }) => {
  if (!accum.includes(currency)) accum.push(currency);

  return accum;
}, []);

const paymentsPendingBalance = ({ operation, voucherTotal, voucherCurrency }) => (currency) => {
  if (currency !== voucherCurrency) return 0;

  const payed = operation.payments.reduce((accum, { currency: paymentCurrency, amount }) => {
    if (paymentCurrency === currency) return accum + amount;

    return accum;
  }, 0);

  return voucherTotal - payed;
};

const paymentsExpired = ({ operation }) => moment(operation.expirationDate) < moment();

export default {
  paymentsCurrencies,
  paymentsPendingBalance,
  paymentsExpired,
  fixedConcepts,
  fixedDocumentsRelated,
  fixedPayments,
  transfers,
  retained,
  localTransfers,
  localRetained,
  totalTaxesTransferred,
  totalLocalTaxesTransferred,
  totalTaxesWithheld,
  totalLocalTaxesWithHold,
  subTotal,
  subTotalCurrency,
  taxes,
  taxesCurrency,
  total,
  totalCurrency,
  voucherDateToInput: ({ voucherDate }) => moment(voucherDate).format('YYYY-MM-DDTHH:mm'),
  iva: (state, getters) => getters.total - getters.subTotal,
  ivaCurrency: ({ voucherCurrency }, { iva }) => utils.toCurrency({ iva }, voucherCurrency),
};
